import { postTimeEntry } from '@/api/reports'
import Btn from '@/components/forms/Btn'
import globalContext from '@/context'
import { useFilledTimeEntry, useSubmit } from '@/hooks'
import { useContext, useState } from 'preact/hooks'
import { useTranslation } from 'react-i18next'
import { FuzzySelect, ProjectWithTaskSelect } from './forms/FuzzySelect'
import TimeCard from './forms/TimeCard'
import TravelingList from './forms/TravelingList'
import Input from './forms/Input'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getTasks } from '@/api/resources'
import { isQueryLoading } from '@/helpers'
import { Fragment, JSX } from 'preact'
import { TimeEntryType, TimeEntryForm } from '@/types'
import { FC } from 'preact/compat'
import { route } from 'preact-router'
import { Api } from '@/types'
import { formDataToJson, appendFormData } from '@/utils'

type Props = {
	type: TimeEntryType
	timeEntry: TimeEntryForm | undefined
	translation: string
	withTraveling?: boolean
	submitArgs?: Partial<Parameters<typeof useSubmit>[0]>
	disabled?: boolean
} & JSX.HTMLAttributes<HTMLFormElement>

const DailyReport: FC<Props> = (p) => {
	const { t } = useTranslation(p.translation)
	const [s, actions] = useContext(globalContext)
	const queryClient = useQueryClient()
	const timeEntry = useFilledTimeEntry(p.timeEntry?.id || 0)
	const [project, setProject] = useState<Api.Project | undefined>(undefined)
	const projectId = project?.id
	const {
		status: tasksStatus,
		fetchStatus: tasksFetchStatus,
		data: tasks,
	} = useQuery({
		queryKey: ['tasks'],
		queryFn: getTasks,
	})

	if (p.timeEntry && !project) {
		setProject(p.timeEntry.project)
	}

	const mutation = useMutation({
		mutationKey: ['create-time-entry'],
		mutationFn: (data: any) => {
			const formData = new FormData()

			appendFormData(formData, data);
			return postTimeEntry(formData)
		},
		onSuccess: (data, variables, context) => {
			actions.toastInfo(t('toasts:form_submitted'));
			setTimeout(() => {
				route('/reports');
				scrollTo({ top: 0 });
			}, 1000);
			queryClient.invalidateQueries({
				queryKey: ['time-entries']
			})
		},
		onError: (error, variables, context) => {
			actions.toastError(t('toasts:generic_error'))
			setTimeout(() => {
				route('/reports')
			}, 1000)
		}
	})

	const onSubmit = async (event: SubmitEvent) => {
		event.preventDefault();

		let form = new FormData(event.target as HTMLFormElement);
		if (p.submitArgs?.process) {
			await p.submitArgs.process(form);
		}
		mutation.mutate(formDataToJson(form));
	}

	return (
		<Fragment>
			<h1>{t(`routes:${p.type}`)}</h1>
			<form onSubmit={onSubmit}>
				<fieldset className='dummy' disabled={p.disabled}>
					<input type="hidden" name="id" value={p.timeEntry?.id} />
					<input type="hidden" name="myoctans_type" value={p.type} />
					<ProjectWithTaskSelect
						required
						name='project_id'
						label={t('project')}
						onMatch={(match: any) => setProject(match as Api.Project)}
						defaultValue={p.timeEntry?.project_id?.toString()}
						disabled={p.type == 'planned_report' ? true : false}
					/>
					<FuzzySelect<Api.Task>
						required
						name='task_id'
						label={t('task')}
						disabled={p.type == 'planned_report' ? true : false}
						resource={tasks?.filter(task => task.project_id == projectId)}
						selectedId={p.timeEntry?.task_id}
						isLoading={isQueryLoading(tasksStatus, tasksFetchStatus)}
					/>
					<TimeCard report={p.timeEntry} />
					{
						p.withTraveling &&
						<TravelingList
							travelings={p.timeEntry?.transport}
							translation='daily_report'
						/>
					}
					{
						p.type != 'planned_report' && p.type != 'simplified_daily' ?
							<>
								<Input
									units='minutes'
									name='lunch_time'
									label={t('lunch_time')}
									defaultValue={p.timeEntry?.lunch_time?.toString()}
								/>
								<Input
									className='span3'
									units='minutes'
									name='break_time'
									label={t('break_time')}
									defaultValue={p.timeEntry?.break_time?.toString()}
								/>
								<Input
									className='span3'
									units='hours'
									name='lost_time'
									label={t('lost_time')}
									defaultValue={p.timeEntry?.lost_time?.toString()}
								/>
							</> : ''
					}
					{
						p.type == 'simplified_daily' ?
							<Input
								class='span6'
								units='hours'
								name='traveling_time'
								label={t('traveling_hours')}
								defaultValue={p.timeEntry?.traveling_time?.toString()}
							/> : ''
					}

					{p.children}
					{!p.disabled && (
						<Btn submit disabled={mutation.isPending}>
							{t('forms:submit')}
						</Btn>
					)}
				</fieldset>
			</form>
		</Fragment>
	)

}
export default DailyReport
